/**
 * SnapshotHistoryView - Snapshot List for a Series
 *
 * Displays snapshots grouped by Year > Month with accordion navigation.
 * - Years are shown as collapsible sections
 * - Current year is expanded by default
 * - Months within a year are accordion-collapsible
 *
 * Accessed through a parent series, never directly.
 */

import React from 'react';
import {
  ArrowLeft,
  Clock,
  FileText,
  Calendar,
  Loader2,
  AlertCircle,
  Settings,
  Play,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Folder,
  ChevronsUpDown,
  ChevronsDownUp
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSnapshotHistory } from '../../../hooks/useSnapshotHistory.js';
import { FREQUENCY_LABELS } from '../../../hooks/useReportSeries.js';
import SnapshotHistoryItem from './SnapshotHistoryItem.jsx';

export default function SnapshotHistoryView({
  seriesId,
  onBack,
  onDownload
}) {
  const { t } = useTranslation();
  const {
    series,
    snapshotsByYear,
    pagination,
    loading,
    error,
    selectedYear: _selectedYear,
    expandedMonths: _expandedMonths,
    reload,
    toggleYear,
    toggleMonth,
    expandAllMonths,
    collapseAllMonths,
    isYearExpanded,
    isMonthExpanded,
    isLatest
  } = useSnapshotHistory(seriesId);

  // Format month range for collapsed year display
  const formatMonthRange = (yearData) => {
    if (!yearData.months || yearData.months.length === 0) return '';
    const months = yearData.months.map(m => m.label);
    if (months.length === 1) return months[0];
    return `${months[months.length - 1]} - ${months[0]}`;
  };

  // Get current year
  const currentYear = new Date().getFullYear();

  // Render loading state
  if (loading && !series) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-600">{t('reports.snapshots.loading')}</span>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-red-500">
        <AlertCircle className="w-12 h-12 mb-4" />
        <p className="text-lg font-medium">{t('common.states.loadError')}</p>
        <p className="text-sm text-gray-500 mt-2">{error}</p>
        <button
          onClick={reload}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header with Back Button */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('reports.snapshots.backToSeries')}</span>
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {series?.name || t('reports.snapshots.seriesFallback')}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {t(`reports.schedule.frequencies.${series?.frequency}.label`, { defaultValue: FREQUENCY_LABELS[series?.frequency] || series?.frequency })}
              </span>
              <span className="flex items-center gap-1.5">
                <FileText className="w-4 h-4" />
                {t('reports.series.snapshotsCount', { count: pagination.total })}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {t('reports.snapshots.yearsCount', { count: snapshotsByYear.length })}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
              <Settings className="w-5 h-5" />
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              <Play className="w-4 h-4" />
              {t('reports.series.generateNow')}
            </button>
          </div>
        </div>
      </div>

      {/* Year Accordion Navigation */}
      <div className="space-y-3">
        {snapshotsByYear.map((yearData) => {
          const isCurrentYear = yearData.year === currentYear;
          const isExpanded = isYearExpanded(yearData.year);

          return (
            <div
              key={yearData.year}
              className={`border rounded-lg overflow-hidden transition-all ${
                isExpanded ? 'border-blue-300 shadow-sm' : 'border-gray-200'
              }`}
            >
              {/* Year Header */}
              <button
                onClick={() => toggleYear(yearData.year)}
                className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
                  isExpanded
                    ? 'bg-blue-50 hover:bg-blue-100'
                    : 'bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <FolderOpen className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Folder className="w-5 h-5 text-gray-400" />
                  )}
                  <span className={`text-lg font-semibold ${
                    isExpanded ? 'text-blue-900' : 'text-gray-900'
                  }`}>
                    {yearData.year}
                  </span>
                  {isCurrentYear && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                      {t('reports.snapshots.currentYear')}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  {/* Summary when collapsed */}
                  {!isExpanded && (
                    <span className="text-sm text-gray-500">
                      {t('reports.series.snapshotsCount', { count: yearData.totalSnapshots })} • {formatMonthRange(yearData)}
                    </span>
                  )}
                  <span className={`px-2.5 py-1 text-sm font-medium rounded-full ${
                    isExpanded ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {t('reports.snapshots.monthsCount', { count: yearData.months.length })}
                  </span>
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-blue-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Year Content - Month Accordions */}
              {isExpanded && (
                <div className="border-t border-blue-200 bg-gray-50">
                  {/* Expand/Collapse All Months */}
                  <div className="flex items-center justify-end gap-2 p-2 border-b border-gray-200 bg-white">
                    <button
                      onClick={expandAllMonths}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <ChevronsUpDown className="w-3.5 h-3.5" />
                      {t('reports.snapshots.expandAll')}
                    </button>
                    <button
                      onClick={collapseAllMonths}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <ChevronsDownUp className="w-3.5 h-3.5" />
                      {t('reports.snapshots.collapseAll')}
                    </button>
                  </div>

                  {/* Month List */}
                  <div className="divide-y divide-gray-200">
                    {yearData.months.map((monthData) => {
                      const isMonthOpen = isMonthExpanded(monthData.key);

                      return (
                        <div key={monthData.key} className="bg-white">
                          {/* Month Header */}
                          <button
                            onClick={() => toggleMonth(monthData.key)}
                            className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                              isMonthOpen
                                ? 'bg-gray-50 border-l-4 border-blue-500'
                                : 'hover:bg-gray-50 border-l-4 border-transparent'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {isMonthOpen ? (
                                <ChevronDown className="w-4 h-4 text-blue-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                              <span className={`font-medium capitalize ${
                                isMonthOpen ? 'text-blue-900' : 'text-gray-700'
                              }`}>
                                {monthData.label}
                              </span>
                            </div>

                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              isMonthOpen
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {t('reports.schedule.reportWord', { count: monthData.snapshots.length })}
                            </span>
                          </button>

                          {/* Month Content - Snapshot List */}
                          {isMonthOpen && (
                            <div className="px-4 py-3 space-y-2 bg-gray-50 border-l-4 border-blue-500">
                              {monthData.snapshots.map(snapshot => (
                                <SnapshotHistoryItem
                                  key={snapshot.id}
                                  snapshot={snapshot}
                                  isLatest={isLatest(snapshot.id)}
                                  onDownload={onDownload}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {snapshotsByYear.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <FileText className="w-16 h-16 mb-4 text-gray-300" />
          <p className="text-lg font-medium">{t('reports.snapshots.emptyTitle')}</p>
          <p className="text-sm mt-2">{t('reports.snapshots.emptyHint')}</p>
          <button className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Play className="w-4 h-4" />
            {t('reports.snapshots.generateFirst')}
          </button>
        </div>
      )}

      {/* Loading indicator for more data */}
      {loading && snapshotsByYear.length > 0 && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          <span className="ml-2 text-sm text-gray-500">{t('common.states.loading')}</span>
        </div>
      )}
    </div>
  );
}
